/**
 * Basis-Validierungs-Service für Feldvalidierungen und Kommentare.
 * Kann direkt verwendet oder erweitert werden.
 * Optional überschreibbar: getCurrentUser(), saveContext().
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IValidationService } from '../../core/interfaces';
import {
	CheckerComments,
	ValidationLevel,
	ValidationResult,
	ValidatorFunction,
	ValidationState
} from '../../core/types';
import { kd } from '../../core/utils';

@Injectable()
export class BaseValidationService implements IValidationService {
	/** Zeigt an ob Änderungen vorliegen */
	dirty = new BehaviorSubject<boolean>(false);

	/** Emittiert den Feldnamen wenn Kommentare für ein Feld geändert werden */
	commentsChanged = new BehaviorSubject<string | null>(null);

	/** Zeigt an ob Validierungsmeldungen oder Kommentare vorhanden sind */
	has = new BehaviorSubject<boolean>(false);

	/** Registrierte Fehler (für Template-Zugriff) */
	danger_ = new Map<string, string>();
	/** Registrierte Warnungen (für Template-Zugriff) */
	warning_ = new Map<string, string>();
	/** Registrierte Hinweise (für Template-Zugriff) */
	message_ = new Map<string, string>();

	protected validators = new Map<string, ValidatorFunction>();
	protected context: unknown;

	/**
	 * Setzt den Kontext (z.B. das aktuelle Dokument)
	 */
	setContext(ctx: unknown): void {
		const oldId = (this.context as { _id?: string })?._id;
		const newId = (ctx as { _id?: string })?._id;
		if (oldId !== newId) {
			this.dirty.next(false);
			this.danger_.clear();
			this.warning_.clear();
			this.message_.clear();
			this.has.next(this.hasAny());
		}
		this.context = ctx;
	}

	/**
	 * Registriert einen Validator für einen Feldnamen
	 */
	registerValidator(key: string, func: ValidatorFunction): void {
		this.validators.set(key, func);
	}

	// Factory-Methoden für ValidationResult
	danger(msg: string): ValidationResult {
		return ValidationResult.danger(msg);
	}

	warning(msg: string): ValidationResult {
		return ValidationResult.warning(msg);
	}

	message(msg: string): ValidationResult {
		return ValidationResult.message(msg);
	}

	ok(): ValidationResult {
		return ValidationResult.ok();
	}

	/**
	 * Prüft ob Validierungsmeldungen vorhanden sind
	 */
	empty(): boolean {
		return this.danger_.size === 0 && this.warning_.size === 0 && this.message_.size === 0;
	}

	/**
	 * Prüft ob irgendwelche Meldungen oder Kommentare vorhanden sind
	 */
	hasAny(): boolean {
		return !this.empty() || this.hasCheckerComments();
	}

	/**
	 * Prüft ob Validierungsmeldungen vorhanden sind
	 */
	hasMessages(): boolean {
		return !this.empty();
	}

	/**
	 * Setzt den ValidationState am Kontext
	 */
	setValidationState(): void {
		if (!this.context) return;
		const cmts = this.checkerComments();
		const hasCmts = cmts && cmts.size > 0;
		const val: ValidationState = {
			dataSeverity: ValidationLevel.ok,
			commentsSeverity: ValidationLevel.ok,
			hasDiskussions: !!hasCmts
		};
		if (this.danger_.size > 0) val.dataSeverity = ValidationLevel.danger;
		else if (this.warning_.size > 0) val.dataSeverity = ValidationLevel.warning;
		cmts?.forEach((comment: CheckerComments) => {
			if (!comment.resolved || val.resolved === undefined)
				val.resolved = comment.resolved;
			val.commentsSeverity = Math.max(val.commentsSeverity, comment.type);
		});
		const ctx = this.context as { state?: ValidationState };
		if (!this.isEqual(ctx.state, val)) {
			ctx.state = val;
		}
	}

	/**
	 * Prüft ob Checker-Kommentare vorhanden sind
	 */
	hasCheckerComments(): boolean {
		const cmts = this.checkerComments();
		return !!cmts && (cmts.size > 0);
	}

	/**
	 * Gibt alle Checker-Kommentare zurück
	 */
	checkerComments(): Map<string, CheckerComments> | undefined {
		const ctx = this.context as { checkerComments?: Map<string, CheckerComments> | Record<string, CheckerComments> };
		let cmts = ctx?.checkerComments;
		if (cmts && !(cmts instanceof Map)) {
			if (cmts instanceof Object) cmts = new Map<string, CheckerComments>(Object.entries(cmts));
			if (cmts.size === 0) return undefined;
		}
		return cmts as Map<string, CheckerComments>;
	}

	/**
	 * Gibt die Kommentare für ein Feld zurück
	 */
	hasComments(key: string): CheckerComments | false {
		const ctx = this.context as { checkerComments?: Map<string, CheckerComments> | Record<string, CheckerComments> };
		if (!ctx || !ctx.checkerComments) return false;
		const c = ctx.checkerComments;
		if (c instanceof Map) {
			if (!c.has(key)) return false;
			return c.get(key)!;
		} else if (this.isObject(c)) {
			if (!Object.keys(c).includes(key)) return false;
			return (c as Record<string, CheckerComments>)[key]!;
		}
		return false;
	}

	/**
	 * Gibt die höchste Kommentar-Schwere für alle Felder zurück, die mit dem Präfix beginnen
	 */
	getMaxCommentSeverityByPrefix(prefix: string): ValidationLevel {
		const ctx = this.context as { checkerComments?: Map<string, CheckerComments> | Record<string, CheckerComments> };
		if (!ctx || !ctx.checkerComments) return ValidationLevel.ok;
		const c = ctx.checkerComments;
		let maxSeverity = ValidationLevel.ok;

		const checkComment = (key: string, comment: CheckerComments) => {
			if (key.startsWith(prefix) && comment && !comment.resolved) {
				if (comment.type > maxSeverity) {
					maxSeverity = comment.type;
				}
			}
		};

		if (c instanceof Map) {
			c.forEach((comment, key) => checkComment(key, comment));
		} else if (this.isObject(c)) {
			Object.entries(c).forEach(([key, comment]) => checkComment(key, comment as CheckerComments));
		}

		return maxSeverity;
	}

	/**
	 * Validiert einen Feldwert
	 */
	validate(key: string, data: unknown): ValidationResult {
		const checkerComments = this.hasComments(key);

		const checkerResult = !checkerComments
			? this.ok()
			: (checkerComments.type === ValidationLevel.ok
				? this.message('')
				: (checkerComments.type === ValidationLevel.warning
					? this.warning('')
					: this.danger('')
				)
			);
		if (!this.validators.has(kd(key).key)) return checkerResult;

		const func = this.validators.get(kd(key).key);
		const validationResult = func!(data, this.context);
		if (checkerResult.type < validationResult.type) return validationResult;
		else if (checkerResult.type > validationResult.type) return checkerResult;
		else if (checkerResult.type === ValidationLevel.ok && checkerResult.msg) return checkerResult;
		else return validationResult;
	}

	/**
	 * Registriert eine Fehlermeldung für ein Feld
	 */
	registerDanger(key: string, msg: string): void {
		this.danger_.set(key, msg);
		this.has.next(true);
	}

	/**
	 * Registriert eine Warnung für ein Feld
	 */
	registerWarning(key: string, msg: string): void {
		this.warning_.set(key, msg);
		this.has.next(true);
	}

	/**
	 * Registriert eine Info-Nachricht für ein Feld
	 */
	registerMessage(key: string, msg: string): void {
		this.message_.set(key, msg);
		this.has.next(true);
	}

	/**
	 * Stellt sicher dass die Kommentar-Map existiert
	 */
	protected ensureComments(): boolean {
		if (!this.context) return false;
		const ctx = this.context as { checkerComments?: Map<string, CheckerComments> | Record<string, CheckerComments> };
		if (!ctx.checkerComments)
			ctx.checkerComments = new Map<string, CheckerComments>();
		let cmts = ctx.checkerComments;
		if (cmts && !(cmts instanceof Map)) {
			cmts = new Map<string, CheckerComments>(Object.entries(cmts));
			ctx.checkerComments = cmts;
		}
		return true;
	}

	/**
	 * Registriert einen Fehler-Kommentar
	 * Die Subklasse muss getCurrentUser() implementieren
	 */
	registerDangerComment(key: string, msg: string): void {
		if (!this.ensureComments()) return;
		const ctx = this.context as { checkerComments: Map<string, CheckerComments> };
		ctx.checkerComments.set(key, {
			resolved: false,
			type: ValidationLevel.danger,
			comments: [{ name: this.getCurrentUser(), msg: msg, time: Date.now() }]
		});
		this.has.next(true);
		this.saveContext();
	}

	/**
	 * Registriert einen Warnungs-Kommentar
	 */
	registerWarningComment(key: string, msg: string): void {
		if (!this.ensureComments()) return;
		const ctx = this.context as { checkerComments: Map<string, CheckerComments> };
		ctx.checkerComments.set(key, {
			resolved: false,
			type: ValidationLevel.warning,
			comments: [{ name: this.getCurrentUser(), msg: msg, time: Date.now() }]
		});
		this.has.next(true);
		this.saveContext();
	}

	/**
	 * Registriert einen Info-Kommentar
	 */
	registerMessageComment(key: string, msg: string): void {
		if (!this.ensureComments()) return;
		const ctx = this.context as { checkerComments: Map<string, CheckerComments> };
		ctx.checkerComments.set(key, {
			resolved: false,
			type: ValidationLevel.ok,
			comments: [{ name: this.getCurrentUser(), msg: msg, time: Date.now() }]
		});
		this.has.next(true);
		this.saveContext();
	}

	/**
	 * Setzt Kommentare für ein Feld zurück oder löscht sie
	 */
	resetComments(key: string, cmts?: CheckerComments): void {
		if (!this.ensureComments()) return;
		const ctx = this.context as { checkerComments: Map<string, CheckerComments> };
		if (cmts) ctx.checkerComments.set(key, cmts);
		else ctx.checkerComments.delete(key);
		this.has.next(this.hasAny());
		this.commentsChanged.next(key);
		this.saveContext();
	}

	/**
	 * Löscht alle Kommentare für ein Feld
	 */
	clearComments(key: string): void {
		if (!this.ensureComments()) return;
		const ctx = this.context as { checkerComments: Map<string, CheckerComments> };
		ctx.checkerComments.delete(key);
		this.has.next(this.hasAny());
		this.commentsChanged.next(key);
		this.saveContext();
	}

	/**
	 * Löscht alle Kommentare für ein Feld ohne automatisches Speichern.
	 * Nützlich beim Löschen von Zeilen, wo mehrere Felder gelöscht werden
	 * und das Speichern erst am Ende erfolgen soll (via dirty.next(true)).
	 */
	clearCommentsNoSave(key: string): void {
		if (!this.ensureComments()) return;
		const ctx = this.context as { checkerComments: Map<string, CheckerComments> };
		ctx.checkerComments.delete(key);
		this.has.next(this.hasAny());
		this.commentsChanged.next(key);
	}

	/**
	 * Löscht den Validierungsstatus für ein Feld
	 */
	clearKey(key: string): void {
		this.danger_.delete(key);
		this.warning_.delete(key);
		this.message_.delete(key);
		this.has.next(this.hasAny());
	}

	/**
	 * Löscht alle Validierungsmeldungen
	 */
	clear(): void {
		this.danger_.clear();
		this.warning_.clear();
		this.message_.clear();
		this.has.next(this.hasAny());
	}

	/**
	 * Gibt den aktuellen Benutzernamen zurück.
	 * Default: 'Unbekannt'. App kann überschreiben.
	 */
	protected getCurrentUser(): string { return 'Unbekannt'; }

	/**
	 * Speichert den aktuellen Kontext.
	 * Default: No-Op. App kann überschreiben für DB-Persistierung.
	 */
	protected saveContext(): void { }

	/**
	 * Prüft ob ein Wert ein Object ist
	 */
	private isObject(value: unknown): boolean {
		return value !== null && typeof value === 'object';
	}

	/**
	 * Einfacher Vergleich zweier Objekte
	 */
	private isEqual(a: unknown, b: unknown): boolean {
		return JSON.stringify(a) === JSON.stringify(b);
	}
}
